import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Trash2, IndianRupee, Loader2, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

/**
 * Folding Category2 piece-rate config.
 *
 * Surfaces under SFG Put-Away Entries per ops's request — admins set a
 * per-category2 INR/USN rate for folding staff. Any category2 without
 * an override falls back to the flat `INH_FOLDING / usn` piece-rate.
 *
 * Read-open to all roles; only Admins can mutate (server enforces the gate).
 */
const FoldingCategoryRatesCard = () => {
  const [rates, setRates] = useState([]);
  const [flatFallback, setFlatFallback] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState({ category2: '', rate_per_usn: '', remarks: '' });

  const fetchRates = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get('/hr/folding-category-rates');
      setRates(data.rates || []);
      setFlatFallback(data.flat_fallback || null);
      setIsAdmin(!!data.is_admin);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load folding rates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRates(); }, [fetchRates]);

  const handleSave = async () => {
    const cat = (draft.category2 || '').trim();
    const rate = parseFloat(draft.rate_per_usn);
    if (!cat) { toast.error('Category 2 is required'); return; }
    if (!Number.isFinite(rate) || rate < 0) { toast.error('Rate must be a non-negative number'); return; }
    setSaving(true);
    try {
      await apiClient.post('/hr/folding-category-rates', {
        category2: cat, rate_per_usn: rate, remarks: draft.remarks || null,
      });
      toast.success(`Saved rate for "${cat}"`);
      setDraft({ category2: '', rate_per_usn: '', remarks: '' });
      fetchRates();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (cat) => {
    if (!confirm(`Remove the folding rate for "${cat}"? Wages will fall back to the flat INH_FOLDING rate.`)) return;
    try {
      await apiClient.delete(`/hr/folding-category-rates/${encodeURIComponent(cat)}`);
      toast.success(`Removed rate for "${cat}"`);
      fetchRates();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <Card className="mb-6" data-testid="folding-category-rates-card">
      <CardHeader className="py-3 px-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <IndianRupee className="w-5 h-5" />
            Folding Wage Rates — by Category 2
          </CardTitle>
          <div className="flex items-center gap-2">
            {flatFallback?.rate != null && (
              <Badge variant="outline" className="text-xs" data-testid="folding-flat-fallback">
                Flat fallback: ₹{Number(flatFallback.rate).toFixed(2)} / USN
              </Badge>
            )}
            <Button variant="ghost" size="sm" onClick={fetchRates} disabled={loading}
                    data-testid="folding-rates-refresh-btn">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
        <p className="text-xs text-stone-500 mt-1">
          Per-Category 2 rates win when set. USNs whose item-master category 2
          isn't listed below use the flat <code>INH_FOLDING</code> rate from HR
          → Piece-Rate Master.
        </p>
      </CardHeader>
      <CardContent>
        {isAdmin && (
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3 mb-4 p-3 bg-amber-50 border border-amber-200 rounded">
            <div className="md:col-span-4">
              <Label htmlFor="fcr-category2" className="text-xs">Category 2</Label>
              <Input
                id="fcr-category2"
                data-testid="fcr-category2-input"
                placeholder="e.g. Dhoti, Wedding-Combo"
                value={draft.category2}
                onChange={(e) => setDraft(d => ({ ...d, category2: e.target.value }))}
              />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="fcr-rate" className="text-xs">Rate / USN (₹)</Label>
              <Input
                id="fcr-rate"
                data-testid="fcr-rate-input"
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                value={draft.rate_per_usn}
                onChange={(e) => setDraft(d => ({ ...d, rate_per_usn: e.target.value }))}
              />
            </div>
            <div className="md:col-span-4">
              <Label htmlFor="fcr-remarks" className="text-xs">Remarks (optional)</Label>
              <Input
                id="fcr-remarks"
                data-testid="fcr-remarks-input"
                placeholder="e.g. effective May 2026"
                value={draft.remarks}
                onChange={(e) => setDraft(d => ({ ...d, remarks: e.target.value }))}
              />
            </div>
            <div className="md:col-span-2 flex items-end">
              <Button onClick={handleSave} disabled={saving} className="w-full bg-amber-600 hover:bg-amber-700"
                      data-testid="fcr-save-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                Save Rate
              </Button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-center py-6 text-stone-500"><Loader2 className="w-5 h-5 animate-spin inline" /> Loading…</div>
        ) : rates.length === 0 ? (
          <div className="text-center py-6 text-stone-500" data-testid="folding-rates-empty">
            No per-category 2 rates configured. All folding USNs use the flat fallback.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Category 2</TableHead>
                <TableHead className="text-right">Rate / USN</TableHead>
                <TableHead>Effective From</TableHead>
                <TableHead>Remarks</TableHead>
                <TableHead>Updated By</TableHead>
                {isAdmin && <TableHead className="w-16 text-right">&nbsp;</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rates.map((r) => (
                <TableRow key={r.category2} data-testid={`folding-rate-row-${r.category2}`}>
                  <TableCell className="font-medium">{r.category2}</TableCell>
                  <TableCell className="text-right">₹{Number(r.rate_per_usn).toFixed(2)}</TableCell>
                  <TableCell>{r.effective_from}</TableCell>
                  <TableCell className="text-xs text-stone-600">{r.remarks || '—'}</TableCell>
                  <TableCell className="text-xs text-stone-500">{r.updated_by || '—'}</TableCell>
                  {isAdmin && (
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(r.category2)}
                              data-testid={`fcr-delete-${r.category2}`}>
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};

export default FoldingCategoryRatesCard;
